import { useCallback, useEffect, useRef } from 'react';

import type { NoteTelemetryEventType, SectionEventMetrics } from '../utils/analytics/noteTelemetry';

import {
  attachSectionAnalytics,
  findSectionForElement as sharedFindSectionForElement,
  scanHeadings,
  type TrackedSection as Section,
} from './analytics/sectionTracking';

export type MarkdownSectionAnalyticsOptions = {
  docId: string;
  getDocVersion: () => string;
  onEvent: (type: NoteTelemetryEventType, payload: SectionEventMetrics) => void;
  headingLevels?: number[]; // e.g., [2, 3] for h2 and h3 only
  throttleMs?: number;
  threshold?: number; // IntersectionObserver threshold
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
  // Optional callback to track when text_selected events occur (to count selects per section)
  onTextSelected?: (sectionId: string | null) => void;
  // Optional callback to track when text_copied events occur (to count copies per section)
  onTextCopied?: (sectionId: string | null) => void;
};

// Section type is imported from shared tracking utility

/**
 * Hook to track section-level analytics for markdown content with headings
 * Infers sections from markdown headings and tracks rendered/hovered events
 */
export function useMarkdownSectionAnalytics(
  editorRootRef: React.RefObject<HTMLDivElement>,
  opts: MarkdownSectionAnalyticsOptions,
) {
  const headingLevels = opts.headingLevels ?? [1, 2, 3, 4, 5, 6];
  const throttleMs = opts.throttleMs ?? 100;
  const threshold = opts.threshold ?? 0.1;

  const sectionsRef = useRef<Section[]>([]);
  const renderedSectionsRef = useRef<Set<string>>(new Set());
  const currentHoveredSectionRef = useRef<string | null>(null);
  const hoverStartTimeRef = useRef<number | null>(null);
  const lastThrottleTimeRef = useRef<number>(0);

  // Track interaction counts per section (clicks, selects, copies)
  const sectionInteractionCountsRef = useRef<
    Map<string, { clicks: number; selects: number; copies: number }>
  >(new Map());

  const INTERACTION_THRESHOLD_MS = 2000; // 2 seconds

  // Find all headings in the editor DOM
  const findHeadings = useCallback((): Section[] => {
    const root = editorRootRef.current;
    if (!root) return [];
    return scanHeadings(root, headingLevels, opts.docId);
  }, [editorRootRef, headingLevels, opts.docId]);

  // Find which section a DOM element belongs to
  const findSectionForElement = useCallback(
    (element: HTMLElement | null): Section | null => {
      const root = editorRootRef.current;
      if (!root) return null;
      return sharedFindSectionForElement(root, sectionsRef.current, element);
    },
    [editorRootRef],
  );

  // Throttle helper
  const throttle = useCallback(
    (fn: () => void) => {
      const now = performance.now();
      if (now - lastThrottleTimeRef.current >= throttleMs) {
        lastThrottleTimeRef.current = now;
        fn();
      }
    },
    [throttleMs],
  );

  // Handle mouse move to track current section
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      throttle(() => {
        const target = event.target as HTMLElement | null;
        if (!target || !editorRootRef.current?.contains(target)) {
          return;
        }

        const section = findSectionForElement(target);
        if (!section) return;

        const sectionId = section.id;

        // If we're entering a new section
        if (currentHoveredSectionRef.current !== sectionId) {
          // If we were hovering a previous section, check threshold and fire event on leave
          if (currentHoveredSectionRef.current && hoverStartTimeRef.current !== null) {
            const duration = performance.now() - hoverStartTimeRef.current;

            // Only fire event if duration >= threshold
            if (duration >= INTERACTION_THRESHOLD_MS) {
              const currentSection = sectionsRef.current.find(
                s => s.id === currentHoveredSectionRef.current,
              );

              // Get interaction counts for this section
              const counts = sectionInteractionCountsRef.current.get(
                currentHoveredSectionRef.current,
              ) || {
                clicks: 0,
                selects: 0,
                copies: 0,
              };

              opts.onEvent('section_interacted', {
                docId: opts.docId,
                docVersion: opts.getDocVersion(),
                metadata: {
                  section_id: currentHoveredSectionRef.current,
                  section_title: currentSection?.label,
                },
                hoverDuration: duration,
                interactionCounts: {
                  clicks: counts.clicks,
                  selects: counts.selects,
                  copies: counts.copies,
                },
              });

              // Clear counts for this section after firing event
              sectionInteractionCountsRef.current.delete(currentHoveredSectionRef.current);
            }
          }

          // Start tracking new section
          currentHoveredSectionRef.current = sectionId;
          hoverStartTimeRef.current = performance.now();
        }
      });
    },
    [findSectionForElement, throttle, opts, editorRootRef],
  );

  // Handle mouse leave to fire interacted event if threshold met
  const handleMouseLeave = useCallback(() => {
    if (currentHoveredSectionRef.current && hoverStartTimeRef.current !== null) {
      const duration = performance.now() - hoverStartTimeRef.current;

      // Only fire event if duration >= threshold
      if (duration >= INTERACTION_THRESHOLD_MS) {
        const currentSection = sectionsRef.current.find(
          s => s.id === currentHoveredSectionRef.current,
        );

        // Get interaction counts for this section
        const counts = sectionInteractionCountsRef.current.get(
          currentHoveredSectionRef.current,
        ) || {
          clicks: 0,
          selects: 0,
          copies: 0,
        };

        opts.onEvent('section_interacted', {
          docId: opts.docId,
          docVersion: opts.getDocVersion(),
          metadata: {
            ...opts.metadata,
            section_id: currentHoveredSectionRef.current,
            section_title: currentSection?.label,
          },
          hoverDuration: duration,
          interactionCounts: {
            clicks: counts.clicks,
            selects: counts.selects,
            copies: counts.copies,
          },
        });

        // Clear counts for this section after firing event
        sectionInteractionCountsRef.current.delete(currentHoveredSectionRef.current);
      }
    }
    currentHoveredSectionRef.current = null;
    hoverStartTimeRef.current = null;
  }, [opts]);

  // Set up IntersectionObserver for section_rendered events
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;

    // Initial scan for headings
    sectionsRef.current = findHeadings();

    // If no headings found, treat entire document as one section
    if (sectionsRef.current.length === 0) {
      const firstBlock = root.querySelector('[data-node-type], [data-content-type]') as HTMLElement;
      if (firstBlock) {
        sectionsRef.current = [
          {
            id: `${opts.docId}:root`,
            label: 'Document Root',
            headingElement: firstBlock,
            level: 0,
            index: 0,
          },
        ];
      }
    }

    // Set up IntersectionObserver for each section heading
    const observers: IntersectionObserver[] = [];

    sectionsRef.current.forEach(section => {
      if (renderedSectionsRef.current.has(section.id)) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !renderedSectionsRef.current.has(section.id)) {
              renderedSectionsRef.current.add(section.id);
              opts.onEvent('section_rendered', {
                docId: opts.docId,
                docVersion: opts.getDocVersion(),
                metadata: {
                  ...opts.metadata,
                  section_id: section.id,
                  section_title: section.label,
                },
              });
            }
          });
        },
        {
          threshold,
        },
      );

      observer.observe(section.headingElement);
      observers.push(observer);
    });

    // Set up mouse tracking
    const controller = new AbortController();
    const { signal } = controller;
    root.addEventListener('mousemove', handleMouseMove, { signal });
    root.addEventListener('mouseleave', handleMouseLeave, { signal });

    // Track clicks within sections
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !root.contains(target)) return;

      const section = findSectionForElement(target);
      if (!section) return;

      // Increment click count for this section
      const counts = sectionInteractionCountsRef.current.get(section.id) || {
        clicks: 0,
        selects: 0,
        copies: 0,
      };
      counts.clicks += 1;
      sectionInteractionCountsRef.current.set(section.id, counts);
    };

    root.addEventListener('click', handleClick, { signal });

    // Helper function to get section ID for a given element
    const getSectionIdForElement = (element: HTMLElement | null): string | null => {
      if (!element || !root.contains(element)) return null;
      const section = findSectionForElement(element);
      return section?.id || null;
    };

    // Helper to track text_selected events
    const trackTextSelected = (sectionId: string | null) => {
      if (!sectionId) return;

      const counts = sectionInteractionCountsRef.current.get(sectionId) || {
        clicks: 0,
        selects: 0,
        copies: 0,
      };
      counts.selects += 1;
      sectionInteractionCountsRef.current.set(sectionId, counts);
    };

    // Helper to get section title from section ID
    const getSectionTitleForId = (sectionId: string | null): string | undefined => {
      if (!sectionId) return undefined;
      const section = sectionsRef.current.find(s => s.id === sectionId);
      return section?.label;
    };

    // Listen for copy events to track copies per section
    const handleCopy = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      try {
        const range = selection.getRangeAt(0);
        const startElement = range.startContainer;

        let element: HTMLElement | null = null;
        if (startElement.nodeType === Node.TEXT_NODE) {
          element = startElement.parentElement;
        } else if (startElement.nodeType === Node.ELEMENT_NODE) {
          element = startElement as HTMLElement;
        }

        const sectionId = getSectionIdForElement(element);
        if (!sectionId) return;

        // Increment copy count for this section
        const counts = sectionInteractionCountsRef.current.get(sectionId) || {
          clicks: 0,
          selects: 0,
          copies: 0,
        };
        counts.copies += 1;
        sectionInteractionCountsRef.current.set(sectionId, counts);
      } catch {
        // Ignore errors
      }
    };

    // Store helpers on root element so selection analytics can access them via shared bridge
    attachSectionAnalytics(root, sectionsRef as any, {
      trackTextSelected,
    });

    root.addEventListener('copy', handleCopy, { signal });

    // Set up MutationObserver to detect content changes
    let rescanTimer: number | null = null;
    const scheduleRescan = () => {
      if (rescanTimer !== null) {
        clearTimeout(rescanTimer);
      }
      rescanTimer = window.setTimeout(() => {
        // Re-scan headings when content changes
        const newSections = findHeadings();
        if (newSections.length !== sectionsRef.current.length) {
          sectionsRef.current = newSections;
          // Clear rendered set so new sections can fire events
          renderedSectionsRef.current.clear();
        }
        rescanTimer = null;
      }, 150);
    };

    const mutationObserver = new MutationObserver(() => {
      scheduleRescan();
    });

    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
      controller.abort();
      delete (root as any).__sectionAnalytics;
      mutationObserver.disconnect();
      if (rescanTimer !== null) {
        clearTimeout(rescanTimer);
        rescanTimer = null;
      }
    };
  }, [editorRootRef, findHeadings, handleMouseMove, handleMouseLeave, opts, threshold]);

  // Re-scan when document version changes
  useEffect(() => {
    sectionsRef.current = findHeadings();
    // Optionally clear rendered sections to allow re-tracking if needed
    // For now, we keep them tracked once rendered
  }, [opts.getDocVersion(), findHeadings]);
}
